import { supabase } from "@/integrations/supabase/client";

export interface LegalDocumentVersion {
  document_type: string;
  version: string;
  published_at: string;
  requires_reacceptance: boolean;
  change_summary: string | null;
}

export interface LegalAcceptance {
  document_type: string;
  version: string;
  accepted_at: string;
}

/**
 * Get the latest version of a legal document
 */
export async function getLatestVersion(
  documentType: string
): Promise<LegalDocumentVersion | null> {
  const { data, error } = await supabase
    .from("legal_document_versions")
    .select("*")
    .eq("document_type", documentType)
    .order("published_at", { ascending: false })
    .limit(1)
    .single();

  if (error) {
    console.error("Error fetching latest version:", error);
    return null;
  }

  return data;
}

/**
 * Check if user has accepted the latest version of a document
 */
export async function hasAcceptedLatestVersion(
  userId: string,
  documentType: string
): Promise<boolean> {
  const latestVersion = await getLatestVersion(documentType);
  if (!latestVersion) return false;

  const { data, error } = await supabase
    .from("legal_acceptances")
    .select("version")
    .eq("user_id", userId)
    .eq("document_type", documentType)
    .eq("version", latestVersion.version)
    .single();

  if (error) return false;
  return !!data;
}

/**
 * Record user's acceptance of a legal document
 */
export async function recordAcceptance(
  userId: string,
  documentType: string,
  version: string
): Promise<boolean> {
  const { error } = await supabase.from("legal_acceptances").insert({
    user_id: userId,
    document_type: documentType,
    version: version,
    ip_address: null, // Could be collected from headers
    user_agent: navigator.userAgent,
  });

  if (error) {
    console.error("Error recording acceptance:", error);
    return false;
  }

  return true;
}

/**
 * Get all acceptances for a user
 */
export async function getUserAcceptances(
  userId: string
): Promise<LegalAcceptance[]> {
  const { data, error } = await supabase
    .from("legal_acceptances")
    .select("document_type, version, accepted_at")
    .eq("user_id", userId)
    .order("accepted_at", { ascending: false });

  if (error) {
    console.error("Error fetching user acceptances:", error);
    return [];
  }

  return data || [];
}

/**
 * Get documents requiring re-acceptance
 */
export async function getDocumentsRequiringReacceptance(
  userId: string
): Promise<LegalDocumentVersion[]> {
  const { data: versions, error: versionError } = await supabase
    .from("legal_document_versions")
    .select("*")
    .eq("requires_reacceptance", true)
    .order("published_at", { ascending: false });

  if (versionError || !versions) return [];

  const { data: acceptances, error: acceptanceError } = await supabase
    .from("legal_acceptances")
    .select("document_type, version")
    .eq("user_id", userId);

  if (acceptanceError) return versions;

  // Filter out documents that user has already accepted
  const acceptedMap = new Map(
    (acceptances || []).map((a) => [`${a.document_type}:${a.version}`, true])
  );

  return versions.filter(
    (v) => !acceptedMap.has(`${v.document_type}:${v.version}`)
  );
}
